import React, { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: '',
  role: '',
  department: 'Leadership',
  bio: '',
  photo_url: '',
  email: '',
  phone: '',
  display_order: 100,
  is_active: true,
};

const DEPARTMENTS = ['Leadership', 'Programming', 'Production', 'Technical', 'Administration', 'Community Outreach', 'General'];

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = not editing, {} or doc = editing
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const authHeader = `Basic ${localStorage.getItem('crmAuth') || ''}`;

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/crm/staff`, { headers: { Authorization: authHeader } });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      setStaff(data);
    } catch (e) {
      setError(e.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => {
    setForm(EMPTY);
    setEditing({});
  };

  const startEdit = (member) => {
    setForm({
      name: member.name || '',
      role: member.role || '',
      department: member.department || 'General',
      bio: member.bio || '',
      photo_url: member.photo_url || '',
      email: member.email || '',
      phone: member.phone || '',
      display_order: member.display_order ?? 100,
      is_active: member.is_active !== false,
    });
    setEditing(member);
  };

  const cancel = () => { setEditing(null); setForm(EMPTY); setError(''); };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/crm/staff/upload-photo`, {
        method: 'POST',
        headers: { Authorization: authHeader },
        body: fd,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      setForm(f => ({ ...f, photo_url: data.url }));
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.role.trim()) {
      setError('Name and role are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const isEdit = editing && editing.id;
      const url = isEdit ? `${BACKEND_URL}/api/crm/staff/${editing.id}` : `${BACKEND_URL}/api/crm/staff`;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify({ ...form, display_order: Number(form.display_order) || 100 }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Save failed (${res.status})`);
      }
      await load();
      cancel();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this staff member? This cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/crm/staff/${id}`, {
        method: 'DELETE',
        headers: { Authorization: authHeader },
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">👥 Staff Management</h2>
          <p className="text-gray-600 text-sm">Add, edit and publish staff profiles shown on the public Staff page.</p>
        </div>
        {!editing && (
          <button
            onClick={startNew}
            data-testid="staff-add-button"
            className="px-4 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary font-semibold"
          >
            + Add Staff Member
          </button>
        )}
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded border border-red-200" data-testid="staff-error">{error}</div>}

      {editing && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 border border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4">{editing.id ? 'Edit Staff Member' : 'New Staff Member'}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                data-testid="staff-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role / Job Title *</label>
              <input
                type="text"
                data-testid="staff-role-input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                data-testid="staff-department-input"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              >
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input
                type="number"
                data-testid="staff-order-input"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
              <p className="text-xs text-gray-500 mt-1">Lower number appears first.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                data-testid="staff-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                data-testid="staff-phone-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
            <textarea
              rows={4}
              data-testid="staff-bio-input"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
            <div className="flex items-center gap-4">
              {form.photo_url ? (
                <img
                  src={form.photo_url.startsWith('http') ? form.photo_url : `${BACKEND_URL}${form.photo_url}`}
                  alt="preview"
                  className="w-20 h-20 object-cover rounded-lg border"
                />
              ) : (
                <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs">No photo</div>
              )}
              <div>
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  data-testid="staff-photo-input"
                  onChange={handlePhoto}
                  disabled={uploading}
                />
                {uploading && <p className="text-xs text-gray-500 mt-1">Uploading…</p>}
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <input
              id="staff-active"
              type="checkbox"
              data-testid="staff-active-input"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <label htmlFor="staff-active" className="text-sm text-gray-700">Active (visible on public Staff page)</label>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              data-testid="staff-save-button"
              className="px-5 py-2 bg-kioo-primary text-white rounded-lg hover:bg-kioo-secondary font-semibold disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={cancel}
              className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading…</div>
      ) : staff.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-gray-100 text-gray-500">
          No staff yet. Click "Add Staff Member" to create the first profile.
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-4 py-3">Photo</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {staff.map(m => (
                <tr key={m.id} className="border-t border-gray-100" data-testid={`staff-row-${m.id}`}>
                  <td className="px-4 py-2">
                    {m.photo_url ? (
                      <img src={m.photo_url.startsWith('http') ? m.photo_url : `${BACKEND_URL}${m.photo_url}`} alt={m.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                        {(m.name || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 font-medium text-gray-900">{m.name}</td>
                  <td className="px-4 py-2 text-gray-700">{m.role}</td>
                  <td className="px-4 py-2 text-gray-700">{m.department}</td>
                  <td className="px-4 py-2 text-gray-700">{m.display_order}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${m.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {m.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => startEdit(m)}
                      data-testid={`staff-edit-${m.id}`}
                      className="text-kioo-primary hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => remove(m.id)}
                      data-testid={`staff-delete-${m.id}`}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
